import { IS_PROD } from '@/buildInfo';

/*
 * Central logging seam for the Vue app. Every diagnostic that would otherwise
 * call `console.*` routes through here so there is a single place to wire up
 * remote error tracking (Sentry) when that work lands — see the SENTRY SEAM
 * markers below. Keeping the abstraction thin means call sites read the same as
 * a raw `console` call while the `no-console` lint rule guards against new
 * direct usage elsewhere.
 */

/* eslint-disable no-console -- this module is the single sanctioned console boundary */

/**
 * Logs an unexpected error. Use in `catch` blocks for failures the user cannot
 * act on directly (failed mutations, background refreshes, etc.).
 * @param message - human-readable context for what failed
 * @param error - the caught error or related detail, if any
 */
function error(message: string, error?: unknown): void {
  // SENTRY SEAM: forward to captureException(error, { extra: { message } }) here.
  console.error(message, error);
}

/**
 * Logs a non-fatal warning — an operation degraded but recovered (e.g. a
 * third-party lookup failed and the flow fell back to manual entry).
 * @param message - human-readable context for the degraded operation
 * @param detail - optional supporting detail, such as the caught error
 */
function warn(message: string, detail?: unknown): void {
  // SENTRY SEAM: forward to captureMessage(message, 'warning') here.
  console.warn(message, detail);
}

/**
 * Logs an informational message (e.g. the startup banner). Always emitted.
 * @param message - the message to log
 */
function info(message: string): void {
  console.info(message);
}

/**
 * Logs a developer-facing debug trace. Suppressed in production builds so audit
 * and diagnostic noise never reaches end users' consoles.
 * @param message - the debug message to log
 */
function debug(message: string): void {
  if (IS_PROD)
    return;

  console.debug(message);
}

/* eslint-enable no-console */

/** Application logger. Prefer this over direct `console.*` calls. */
export const logger = {
  error,
  warn,
  info,
  debug,
} as const;
